import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  StatCard,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  Button,
  FormGroup,
  Input,
  EmptyState,
  Badge,
  Muted,
  AlertBanner,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface VendorLine {
  vendor_id: string;
  vendor_name: string;
  amount_spent: number;
  po_count: number;
  delivered: boolean;
}

interface BudgetWeek {
  id: string;
  week_start: string;
  week_end: string;
  last_week_sales: number;
  buy_budget: number;
  total_spent: number;
  total_committed: number;
  remaining: number;
  percent_used: number;
  variance: number | null;
  ytd_variance: number | null;
  status: "open" | "closed";
  vendor_lines: VendorLine[];
}

interface BudgetHistoryRow {
  id: string;
  week_start: string;
  week_end: string;
  last_week_sales: number;
  buy_budget: number;
  total_spent: number;
  variance: number;
  ytd_variance: number;
  status: "open" | "closed";
}

// ============================================================
// HELPERS
// ============================================================

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function fmtDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function weekLabel(start: string, end: string): string {
  return `${fmtDate(start)} - ${fmtDate(end)}`;
}

function progressColor(pct: number): string {
  if (pct >= 100) return "var(--rbw-danger)";
  if (pct >= 85) return "var(--rbw-gold)";
  return "var(--rbw-success)";
}

function varianceBadge(v: number) {
  if (v > 0) {
    return <Badge variant="green">{fmt(v)} under</Badge>;
  }
  if (v < 0) {
    return <Badge variant="danger">{fmt(Math.abs(v))} over</Badge>;
  }
  return <Badge>even</Badge>;
}

// ============================================================
// COMPONENT
// ============================================================

export default function BudgetPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const [current, setCurrent] = useState<BudgetWeek | null>(null);
  const [history, setHistory] = useState<BudgetHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSalesInput, setShowSalesInput] = useState(false);
  const [salesInput, setSalesInput] = useState("");
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [curRes, histRes] = await Promise.all([
        api.get("/budget/current"),
        api.get("/budget/history?limit=12"),
      ]);
      setCurrent(curRes.data);
      setHistory(histRes.data);
    } catch {
      setError("Could not load budget data.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSales = async () => {
    if (!current || !salesInput) return;
    try {
      await api.put(`/budget/${current.id}`, {
        last_week_sales: Number(salesInput),
      });
      setSalesInput("");
      setShowSalesInput(false);
      loadData();
    } catch {
      setError("Failed to update sales figure.");
    }
  };

  const handleCloseWeek = async () => {
    if (!current) return;
    if (!window.confirm("Close this week and lock the budget? This will calculate variance and add it to YTD.")) return;
    try {
      setClosing(true);
      await api.post(`/budget/${current.id}/close`);
      loadData();
    } catch {
      setError("Failed to close week.");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Weekly Budget" subtitle="Loading..." />
      </PageContainer>
    );
  }

  if (error && !current) {
    return (
      <PageContainer>
        <PageHeader title="Weekly Budget" />
        <AlertBanner variant="danger">{error}</AlertBanner>
      </PageContainer>
    );
  }

  // Derived values
  const sales = current?.last_week_sales ?? 0;
  const budget = current?.buy_budget ?? 0;
  const spent = current?.total_spent ?? 0;
  const committed = current?.total_committed ?? 0;
  const remaining = current?.remaining ?? 0;
  const pctUsed = current?.percent_used ?? 0;
  const vendors = current?.vendor_lines ?? [];
  const isClosed = current?.status === "closed";

  return (
    <PageContainer>
      <PageHeader
        title="Weekly Budget"
        subtitle={
          current
            ? `Week of ${weekLabel(current.week_start, current.week_end)}`
            : "Patrick's buying budget"
        }
        action={
          isManager && current && !isClosed ? (
            <Button variant="secondary" onClick={handleCloseWeek} disabled={closing}>
              {closing ? "Closing..." : "Close Week"}
            </Button>
          ) : undefined
        }
      />

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}

      {/* ====== THIS WEEK'S BUDGET ====== */}
      <Card className="mb-6">
        <CardHeader
          title="This Week's Budget"
          action={
            isManager && !isClosed ? (
              showSalesInput ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={salesInput}
                    onChange={setSalesInput}
                    placeholder="Last week's sales"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                  <Button size="sm" onClick={handleUpdateSales}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowSalesInput(false)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSalesInput(String(sales || ""));
                    setShowSalesInput(true);
                  }}
                >
                  Edit Sales
                </Button>
              )
            ) : undefined
          }
        />

        {isClosed && (
          <AlertBanner variant="blue">
            This week is closed. Variance: {current?.variance != null ? fmt(current.variance) : "--"}
          </AlertBanner>
        )}

        {/* Formula display */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div
            className="rounded-xl px-5 py-3"
            style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: "var(--rbw-gray-dark)" }}
            >
              Last Week's Sales
            </div>
            <div
              className="text-2xl font-bold"
              style={{ color: "var(--rbw-navy)" }}
            >
              {fmt(sales)}
            </div>
          </div>

          <div
            className="text-2xl font-bold"
            style={{ color: "var(--rbw-gray-dark)" }}
          >
            x 0.65 =
          </div>

          <div
            className="rounded-xl px-5 py-3"
            style={{
              background: "#F6BE0010",
              border: "1px solid #F6BE0030",
            }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: "var(--rbw-gold-dark)" }}
            >
              Buy Budget
            </div>
            <div
              className="text-2xl font-bold"
              style={{ color: "var(--rbw-navy)" }}
            >
              {fmt(budget)}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between mb-1.5">
            <span
              className="text-sm font-medium"
              style={{ color: "var(--rbw-navy)" }}
            >
              {Math.round(pctUsed)}% of budget used
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: progressColor(pctUsed) }}
            >
              {fmt(remaining)} remaining
            </span>
          </div>
          <div
            className="w-full h-4 rounded-full overflow-hidden"
            style={{ background: "var(--rbw-gray-light)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(pctUsed, 100)}%`,
                background: progressColor(pctUsed),
              }}
            />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            label="Spent"
            value={fmt(spent)}
            detail="Delivered POs"
            variant="default"
          />
          <StatCard
            label="Committed"
            value={fmt(committed)}
            detail="Open / pending POs"
            variant="blue"
          />
          <StatCard
            label="Remaining"
            value={fmt(remaining)}
            variant={remaining < 0 ? "danger" : remaining < budget * 0.15 ? "gold" : "green"}
          />
        </div>
      </Card>

      {/* ====== VENDOR BREAKDOWN ====== */}
      <Card className="mb-6">
        <CardHeader title="Vendor Breakdown" />
        {vendors.length === 0 ? (
          <EmptyState
            message="No purchase orders this week"
            detail="POs will appear here as they are submitted"
            icon="📋"
          />
        ) : (
          <Table>
            <Thead>
              <Th>Vendor</Th>
              <Th align="center">POs</Th>
              <Th align="center">Delivered</Th>
              <Th align="right">Amount</Th>
            </Thead>
            <tbody>
              {vendors.map((v, i) => (
                <TRow key={v.vendor_id} index={i}>
                  <Td bold>{v.vendor_name}</Td>
                  <Td align="center">{v.po_count}</Td>
                  <Td align="center">
                    {v.delivered ? (
                      <svg
                        className="w-5 h-5 inline-block"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        style={{ color: "var(--rbw-success)" }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <Muted>--</Muted>
                    )}
                  </Td>
                  <Td align="right" bold>
                    {fmt(v.amount_spent)}
                  </Td>
                </TRow>
              ))}
              {/* Total row */}
              <tr
                style={{
                  background: "var(--rbw-bg)",
                  borderTop: "2px solid var(--rbw-border)",
                }}
              >
                <td
                  className="px-5 py-4 text-sm font-bold"
                  style={{ color: "var(--rbw-navy)" }}
                >
                  Total
                </td>
                <td
                  className="px-5 py-4 text-sm text-center font-bold"
                  style={{ color: "var(--rbw-navy)" }}
                >
                  {vendors.reduce((s, v) => s + v.po_count, 0)}
                </td>
                <td className="px-5 py-4" />
                <td
                  className="px-5 py-4 text-sm text-right font-bold"
                  style={{ color: "var(--rbw-navy)" }}
                >
                  {fmt(vendors.reduce((s, v) => s + v.amount_spent, 0))}
                </td>
              </tr>
            </tbody>
          </Table>
        )}
      </Card>

      {/* ====== BUDGET HISTORY ====== */}
      <Card>
        <CardHeader title="Budget History" />
        {history.length === 0 ? (
          <EmptyState
            message="No past weeks yet"
            detail="Closed weeks will appear here"
            icon="📊"
          />
        ) : (
          <Table>
            <Thead>
              <Th>Week</Th>
              <Th align="right">Sales Input</Th>
              <Th align="right">Budget</Th>
              <Th align="right">Spent</Th>
              <Th align="right">Variance</Th>
              <Th align="right">YTD Variance</Th>
            </Thead>
            <tbody>
              {history.map((row, i) => (
                <TRow key={row.id} index={i}>
                  <Td>
                    <div className="font-medium">
                      {weekLabel(row.week_start, row.week_end)}
                    </div>
                    {row.status === "open" && (
                      <Badge variant="gold">Open</Badge>
                    )}
                  </Td>
                  <Td align="right">{fmt(row.last_week_sales)}</Td>
                  <Td align="right">{fmt(row.buy_budget)}</Td>
                  <Td align="right">{fmt(row.total_spent)}</Td>
                  <Td align="right">{varianceBadge(row.variance)}</Td>
                  <Td align="right" bold>
                    <span
                      style={{
                        color:
                          row.ytd_variance >= 0
                            ? "var(--rbw-success)"
                            : "var(--rbw-danger)",
                      }}
                    >
                      {fmt(row.ytd_variance)}
                    </span>
                  </Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </PageContainer>
  );
}
